<template>
  <div class="app-container">
    <header>
      <div class="header-left">
        <img alt="Vue logo" src="/logo.png">
        <nav>
          <a href="/" @click.prevent="go('home')" :class="{ active: currentPage === 'home' }">
            {{ t('message.home') }}
          </a> |
          <a href="/about" @click.prevent="go('about')" :class="{ active: currentPage === 'about' }">
            {{ t('message.about') }}
          </a> |
          <a href="/contact" @click.prevent="go('contact')" :class="{ active: currentPage === 'contact' }">
            {{ t('message.contact') }}
          </a> |
          <a href="/sitemap" @click.prevent="go('sitemap')" :class="{ active: currentPage === 'sitemap' }">
            {{ t('message.sitemap') }}
          </a>
        </nav>
      </div>
      <LanguageSwitcher />
    </header>

    <main>
      <h1 v-if="currentPage === 'home'">{{ t('message.products') }}</h1>
      <h1 v-else-if="currentPage === 'about'">{{ t('message.about') }}</h1>
      <h1 v-else-if="currentPage === 'contact'">{{ t('message.contact') }}</h1>
      <h1 v-else-if="currentPage === 'detail'">{{ selectedProduct ? selectedProduct.Title : (detailError ? '' : t('product.detail.loading')) }}</h1>
      <h1 v-else-if="currentPage === 'sitemap'">{{ t('message.sitemap') }}</h1>
      
      <ProductList 
        v-if="currentPage === 'home'" 
        @view-detail="viewProductDetail"
      />
      <template v-else-if="currentPage === 'detail'">
        <div v-if="detailError" class="detail-error">{{ detailError }}</div>
        <ProductDetail 
          v-else
          :product="selectedProduct"
          :current-category="currentCategory"
          @close="closeProductDetail"
        />
      </template>
      <AboutUs v-else-if="currentPage === 'about'" />
      <ContactUs v-else-if="currentPage === 'contact'" />
      <SiteMap v-else-if="currentPage === 'sitemap'" />
    </main>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n'
import { useHead } from '@unhead/vue'
import { ref, watch, computed } from 'vue'
import api from './api.js'
import ProductList from './components/ProductList.vue'
import ProductDetail from './components/ProductDetail.vue'
import LanguageSwitcher from './components/LanguageSwitcher.vue'
import AboutUs from './components/AboutUs.vue'
import ContactUs from './components/ContactUs.vue'
import SiteMap from './components/Sitemap.vue'

export default {
  name: 'App',
  components: {
    ProductList,
    ProductDetail,
    LanguageSwitcher,
    AboutUs,
    ContactUs,
    SiteMap
  },
  setup() {
    const { t, locale } = useI18n()
    const pageFromPath = () => {
      const p = window.location.pathname
      if (p === '/about') return 'about'
      if (p === '/contact') return 'contact'
      if (p === '/sitemap') return 'sitemap'
      if (p.startsWith('/product/')) return 'detail'
      return 'home'
    }
    const productIdFromPath = () => {
      const p = window.location.pathname
      if (p.startsWith('/product/')) {
        return decodeURIComponent(p.substring('/product/'.length))
      }
      return null
    }
    const currentPage = ref(pageFromPath())
    const go = (page) => {
      currentPage.value = page
      const path = page === 'home' ? '/' : '/' + page
      window.history.pushState(null, '', path)
    }
    const selectedProduct = ref(null)
    const currentCategory = ref('xfjMosfet')
    const detailLoading = ref(false)
    const detailError = ref('')

    // 从 URL 中的产品 ID 加载详情（用于直接访问/刷新/浏览器前进后退）
    const loadProductByPath = async () => {
      const id = productIdFromPath()
      if (!id) return
      detailLoading.value = true
      detailError.value = ''
      try {
        const res = await api.get(`/api/product/${encodeURIComponent(id)}`)
        const data = res.data
        if (data && data.success && data.data) {
          selectedProduct.value = data.data
          currentCategory.value = data.data.category || 'xfjMosfet'
          detailError.value = ''
        } else {
          selectedProduct.value = null
          detailError.value = t('product.detail.not_found')
        }
      } catch (e) {
        console.error('加载产品详情失败:', e)
        selectedProduct.value = null
        detailError.value = t('product.detail.load_error')
      } finally {
        detailLoading.value = false
      }
    }

    // 初始化：直接从 /product/:id 进入（如刷新、分享链接、搜索引擎）
    if (currentPage.value === 'detail') {
      loadProductByPath()
    }

    window.addEventListener('popstate', () => {
      currentPage.value = pageFromPath()
      if (currentPage.value === 'detail') {
        // 已显示的产品 ID 与 URL 一致时不重复加载
        const id = productIdFromPath()
        const shownId = selectedProduct.value ? String(selectedProduct.value.id || selectedProduct.value.Whir_U_Content_PId) : ''
        if (!shownId || shownId !== id) {
          selectedProduct.value = null
          loadProductByPath()
        }
      }
    })

    // 全站默认 SEO（产品详情页会在 ProductDetail 中覆盖）
    const SUPPORTED_LOCALES = ['zh-CN', 'en-US', 'vi-VN', 'zh-TW']
    const DEFAULT_LOCALE = 'en-US'
    const getHreflangLinks = () => {
      const origin = window.location.origin
      const path = window.location.pathname
      const links = SUPPORTED_LOCALES.map((loc) => ({
        rel: 'alternate',
        hreflang: loc,
        href: `${origin}${path}?lang=${loc}`
      }))
      links.push({
        rel: 'alternate',
        hreflang: 'x-default',
        href: `${origin}${path}?lang=${DEFAULT_LOCALE}`
      })
      return links
    }

    // 根据当前语言动态设置默认 SEO meta（产品详情页会在 ProductDetail 中覆盖）
    const seoDescription = computed(() => {
      switch (locale.value) {
        case 'zh-CN':
          return '765.info - 专业功率半导体器件供应商，提供 MOSFET、功率模块、DC-DC 等高性能功率器件产品查询与技术支持。'
        case 'zh-TW':
          return '765.info - 專業功率半導體器件供應商，提供 MOSFET、功率模組、DC-DC 等高效能功率器件產品查詢與技術支援。'
        case 'vi-VN':
          return '765.info - Nhà cung cấp linh kiện bán dẫn công suất chuyên nghiệp, cung cấp tra cứu sản phẩm MOSFET, module công suất, DC-DC và hỗ trợ kỹ thuật.'
        default:
          return '765.info - Professional power semiconductor supplier. Query MOSFETs, power modules, DC-DC converters and more with technical support.'
      }
    })
    const seoKeywords = computed(() => {
      switch (locale.value) {
        case 'zh-CN':
        case 'zh-TW':
          return 'MOSFET, 功率半导体, 功率模块, DC-DC, 765.info'
        case 'vi-VN':
          return 'MOSFET, bán dẫn công suất, module công suất, DC-DC, 765.info'
        default:
          return 'MOSFET, power semiconductor, power module, DC-DC, 765.info'
      }
    })

    useHead({
      link: getHreflangLinks(),
      meta: [
        { name: 'description', content: seoDescription },
        { name: 'keywords', content: seoKeywords },
        { property: 'og:title', content: '765.info - Power Semiconductor Devices' },
        { property: 'og:description', content: seoDescription },
        { property: 'og:type', content: 'website' },
        { property: 'og:site_name', content: '765.info' }
      ]
    })
    
    const viewProductDetail = (product, category) => {
      selectedProduct.value = product
      currentCategory.value = category || (product && product.category) || 'xfjMosfet'
      currentPage.value = 'detail'
      detailError.value = ''
      const id = product ? (product.id || product.Whir_U_Content_PId || '') : ''
      // 独立 URL 便于 SEO 收录与分享
      window.history.pushState(null, '', '/product/' + encodeURIComponent(id))
    }
    
    const closeProductDetail = () => {
      currentPage.value = 'home'
      selectedProduct.value = null
      window.history.pushState(null, '', '/')
    }
    
    watch(
      () => currentPage.value,
      (newPage) => {
        let title = '765.info'
        switch (newPage) {
          case 'about':
            title = t('message.about')
            break
          case 'contact':
            title = t('message.contact')
            break
          case 'home':
            title = '765.info'
            break
          case 'detail':
            title = selectedProduct.value ? selectedProduct.value.Title + ' - 765.info' : '产品详情 - 765.info'
            break
          case 'sitemap':
            title = t('message.sitemap') + ' - 765.info'
            break
        }
        document.title = title
      },
      { immediate: true }
    )
    
    return { 
      t,
      currentPage,
      go,
      selectedProduct,
      currentCategory,
      detailLoading,
      detailError,
      viewProductDetail,
      closeProductDetail
    }
  }
}
</script>

<style>
#app {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  margin: 0;
  padding: 0;
  background-color: #f5f7fa;
}

.app-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
}

header {
  background-color: #ffffff;
  padding: 15px 30px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.header-left {
  display: flex;
  align-items: center;
}

header img {
  height: 50px;
  margin-right: 24px;
}

nav {
  display: flex;
  align-items: center;
}

nav a {
  font-weight: 500;
  color: #2c3e50;
  text-decoration: none;
  margin: 0 15px;
  padding: 5px 0;
  position: relative;
}

nav a:hover {
  color: #42b983;
}

nav a:hover::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 2px;
  bottom: 0;
  background-color: #42b983;
  transition: all 0.3s ease;
}

nav a.active {
  color: #42b983;
}

nav a.active::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 2px;
  bottom: 0;
  left: 0;
  background-color: #42b983;
}

main {
  padding: 0 20px 40px;
}

h1 {
  font-size: 1.8em;
  margin: 20px 0;
  color: #2c3e50;
  text-align: center;
}

.detail-error {
  max-width: 600px;
  margin: 40px auto;
  padding: 20px;
  text-align: center;
  color: #c0392b;
  background: #fdf3f2;
  border: 1px solid #e8b4b0;
  border-radius: 6px;
}

button {
  background-color: #42b983;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s ease;
}

button:hover {
  background-color: #3aa876;
}

/* 响应式设计 */
@media (max-width: 768px) {
  header {
    flex-direction: column;
    padding: 15px;
  }

  .header-left {
    flex-direction: column;
    align-items: flex-start;
  }

  header img {
    margin-right: 0;
    margin-bottom: 12px;
  }

  nav {
    width: 100%;
    justify-content: center;
    flex-wrap: wrap;
  }

  nav a {
    margin: 0 10px;
  }
}
</style>